/** Motion blur — directional averaging along a line.
 *
 *  For each output pixel we sample N points along a line of length
 *  `distance` centered on the pixel, oriented at `angle` degrees,
 *  and average the colors. `distance + 1` samples (rounded) gives
 *  ~1 sample per pixel of motion, which is the Photoshop-equivalent
 *  visual density.
 *
 *  Angle convention: 0° = rightward (positive x), 90° = downward
 *  (positive y on screen). Matches Photoshop's UI — the angle dial
 *  ticks clockwise from "3 o'clock". This falls out naturally from
 *  the canvas y-axis already growing downward; no sign flip needed.
 *
 *  Edge handling clamps to source edges — same rationale as
 *  Gaussian: a wrap would seam across the canvas; zero-pad would
 *  darken the perimeter. */

export interface MotionBlurValue {
  /** 0..360. 0 = horizontal blur. */
  angle: number;
  /** Pixels. 0 = identity. */
  distance: number;
}

export const identityMotionBlurValue = (): MotionBlurValue => ({ angle: 0, distance: 0 });

export function applyMotionBlurPixels(source: ImageData, value: MotionBlurValue): ImageData {
  const w = source.width;
  const h = source.height;
  const distance = Math.max(0, value.distance);
  if (distance < 0.5) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  // Half-length each side; samples cover -half..+half along the line.
  const half = distance / 2;
  const samples = Math.max(2, Math.round(distance) + 1);
  const rad = (value.angle * Math.PI) / 180;
  // Per-step pixel offset along the line.
  const stepX = (Math.cos(rad) * distance) / (samples - 1);
  const stepY = (Math.sin(rad) * distance) / (samples - 1);
  // Starting offset relative to current pixel.
  const startX = -Math.cos(rad) * half;
  const startY = -Math.sin(rad) * half;

  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let rAcc = 0;
      let gAcc = 0;
      let bAcc = 0;
      for (let s = 0; s < samples; s++) {
        const sx = Math.min(w - 1, Math.max(0, Math.round(x + startX + stepX * s)));
        const sy = Math.min(h - 1, Math.max(0, Math.round(y + startY + stepY * s)));
        const si = (sy * w + sx) * 4;
        rAcc += src[si];
        gAcc += src[si + 1];
        bAcc += src[si + 2];
      }
      const di = (y * w + x) * 4;
      out[di] = rAcc / samples;
      out[di + 1] = gAcc / samples;
      out[di + 2] = bAcc / samples;
      out[di + 3] = src[di + 3];
    }
  }
  return new ImageData(out, w, h);
}

export function applyMotionBlurToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: MotionBlurValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyMotionBlurPixels(source, value), 0, 0);
}
